import { PageLayout, getPageStatus } from 'components/PageLayout';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import {
  matchPath,
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
} from 'react-router-dom';
import { useProgressionModel } from 'features/wildfires/hooks/useProgressionModel';
import { ProgressionModelMap } from 'features/wildfires/components/ProgressionModelMap';
import { ProgressionModelDrawerContent } from 'features/wildfires/components/ProgressionModelDrawerContent';
import {
  MAP_ENTITY_DRAWER_MIN_SNAP_PCT,
  MapEntityDrawer,
} from 'features/mapping/components/MapEntityDrawer';
import { ProgressionModelSlider } from 'features/wildfires/components/ProgressionModelSlider';
import { useCallback, useEffect, useRef } from 'react';
import { ProgressionModelDialog } from 'features/wildfires/components/ProgressionModelDialog';
import { useSearchDrawerState } from 'state/useSearchDrawerState';
import { useProgressionModelLayersConfig } from 'features/wildfires/hooks/useProgressionModelLayersConfig';
import { useProgressionModelsEnabled } from 'features/wildfires/hooks/useProgressionModelsEnabled';
import {
  BackNavigationCallback,
  ShouldInterceptCondition,
  useInterceptBackNavigation,
} from 'hooks/useInterceptBackNavigation';
import { MapProvider } from 'react-map-gl';
import { parseLatLngFromUrl, useMapPinEnabled } from 'features/mapping/mapPin';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { MapPinDrawerContent } from 'features/mapping/components/MapPinDrawerContent';
import { PROGRESSION_MODEL_LAYER_CONFIG } from 'features/wildfires/wildfire.constants';
import { MEMBERSHIP_PLANS } from 'features/membership/membership.constants';
import { getPaywallLink } from 'features/membership/membership.utils';
import {
  MAP_COORDINATES_ROUTE_PATHS,
  MAP_PIN_ROUTE_PATHS,
  ROUTER_ENTITY_TO_ROUTE_PATH_MAP,
  WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH,
} from '../../constants';

// The progression map's coordinate/viewport and dropped-pin routes: the home
// route templates prefixed with this page's path.
const PROGRESSION_MODEL_COORDINATE_ROUTE_PATHS =
  MAP_COORDINATES_ROUTE_PATHS.map(
    (path) => `${WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH}${path}`,
  );
const PROGRESSION_MODEL_PIN_ROUTE_PATHS = MAP_PIN_ROUTE_PATHS.map(
  (path) => `${WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH}${path}`,
);

type ProgressionModelContentProps = {
  geoEventId: number;
  modelId: number;
};

const MAP_DRAWER_ENTITY_SNAP_POINTS = [
  MAP_ENTITY_DRAWER_MIN_SNAP_PCT,
  0.47,
  0.7,
];

const ProgressionModelContent = (
  props: ProgressionModelContentProps,
): JSX.Element => {
  const { geoEventId, modelId } = props;
  const { open: searchDrawerOpen, setOpen: setSearchDrawerOpen } =
    useSearchDrawerState();
  const searchDrawerOpenRef = useRef(searchDrawerOpen);
  const history = useHistory();
  const showMapPin = useMapPinEnabled();

  const pinImprovements = useReleaseFlag('pin_drop_improvements');

  useProgressionModelLayersConfig(PROGRESSION_MODEL_LAYER_CONFIG);

  /**
   * This is important to correctly position the POI dialog within the map.
   * We save a reference to the initial open value using a ref so it won't
   * change on re-renders and restore this value when the component unmounts.
   */
  useEffect(() => {
    setSearchDrawerOpen(false);
    const initialOpenValue = searchDrawerOpenRef.current;
    return () => {
      setSearchDrawerOpen(initialOpenValue);
    };
  }, [setSearchDrawerOpen]);

  const handleBack: BackNavigationCallback = useCallback(
    (tx) => {
      history.push(tx.pathname + tx.search, { skipMapCentering: true });
    },
    [history],
  );

  const shouldIntercept: ShouldInterceptCondition = useCallback((location) => {
    const navMatch = matchPath(location.pathname, {
      path: ROUTER_ENTITY_TO_ROUTE_PATH_MAP.wildfire,
      exact: true,
    });
    return Boolean(navMatch) && !location.state;
  }, []);

  useInterceptBackNavigation({
    shouldIntercept,
    handleBack,
  });

  return (
    <MapProvider>
      <Switch>
        {/* A dropped pin at specific coordinates. */}
        {showMapPin && (
          <Route
            exact
            path={
              pinImprovements
                ? PROGRESSION_MODEL_PIN_ROUTE_PATHS
                : PROGRESSION_MODEL_COORDINATE_ROUTE_PATHS
            }
            render={(routeProps) => {
              const { latitudeParam, longitudeParam } = parseLatLngFromUrl(
                routeProps.match.params,
              );
              return (
                <>
                  {/* This component must be a child of the Route
                    to access the route's params, which are needed
                    for the map pin marker to work correctly. */}
                  <ProgressionModelMap
                    geoEventId={geoEventId}
                    modelId={modelId}
                  />

                  <MapEntityDrawer
                    snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
                    defaultSnapPointPct={0.47}
                    open
                    entityId={`mapPin-${latitudeParam},${longitudeParam}`}
                    // Make sure pathname doesn't already include lat, lng and zoom params
                    closeRedirectPath={
                      routeProps.location.pathname.split('/@')[0]
                    }
                    preventDrawerClose
                  >
                    <MapPinDrawerContent
                      lat={latitudeParam}
                      lng={longitudeParam}
                    />
                  </MapEntityDrawer>
                </>
              );
            }}
          />
        )}

        {/* The model itself. With no pin dropped, the map centers on the incident. */}
        <Route
          exact
          path={WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH}
          render={() => (
            <>
              <ProgressionModelMap geoEventId={geoEventId} modelId={modelId} />

              <MapEntityDrawer
                snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
                defaultSnapPointPct={MAP_ENTITY_DRAWER_MIN_SNAP_PCT}
                open
                dismissible={false}
                preserveDrawerHeight={false}
                entityId={`progression-model-${modelId}`}
              >
                <ProgressionModelDrawerContent
                  geoEventId={geoEventId}
                  modelId={modelId}
                />
              </MapEntityDrawer>

              <ProgressionModelDialog>
                <ProgressionModelSlider modelId={modelId} />
              </ProgressionModelDialog>
            </>
          )}
        />

        {/* Progression models don't support non-pin coordinate deep links.
            Redirect to the model to make this clear. */}
        {pinImprovements && (
          <Route
            exact
            path={PROGRESSION_MODEL_COORDINATE_ROUTE_PATHS}
            render={(routeProps) => (
              <Redirect to={routeProps.location.pathname.split('/@')[0]} />
            )}
          />
        )}
      </Switch>
    </MapProvider>
  );
};

export const WildfireProgressionModel = (
  props: RouteComponentProps<{ geoEventId: string; modelId: string }>,
): JSX.Element => {
  const {
    match: { params },
  } = props;
  const { t } = useTranslation();
  const showProgressionModels = useProgressionModelsEnabled();

  const geoEventId = parseInt(params.geoEventId, 10);
  const modelId = parseInt(params.modelId, 10);

  const { isLoading, isError, progressionModel } = useProgressionModel({
    modelId,
  });

  const returnRoute = `/i/${geoEventId}?tab=info`;

  if (!showProgressionModels) {
    const paywallLink = getPaywallLink({
      focusedPlan: MEMBERSHIP_PLANS.proYearly,
      source: 'wildfire_progression_model_page_redirect',
    });
    return (
      <Redirect
        to={{
          pathname: paywallLink.pathname,
          search: paywallLink.search,
          state: { fromRoute: returnRoute },
        }}
      />
    );
  }

  if (isLoading || isError || !progressionModel) {
    return (
      <PageLayout
        data-cname="WildfireProgressionModel"
        status={getPageStatus({
          isLoading,
          isError: isError || !progressionModel,
        })}
        headerBarProps={{ action: 'back', forceDark: true }}
      />
    );
  }

  return (
    <PageLayout
      data-cname="WildfireProgressionModel"
      headerBarProps={{ action: returnRoute, forceDark: true }}
    >
      <Helmet>
        <title>
          {t('wildfireProgressionModel.title', {
            name: progressionModel.sourceAttribution,
          })}
        </title>
      </Helmet>
      <ProgressionModelContent geoEventId={geoEventId} modelId={modelId} />
    </PageLayout>
  );
};
